import React from 'react';
import {
  Grid,
  Typography,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import TextInput from '../../../../sharedModules/form/components/TextInput';
import InspectionRadioGroup from '../InspectionRadioGroup';
import { conditionOptions, yesNoOptions } from '../../config/options';
import MediaUploadSection from '../../../../sharedModules/mediaUpload/MediaUploadSection';
import type { InspectionFormValues } from '../../utils/inspectionReportFormDefaults';
import type { ManufacturerOption } from '../../hooks/useHomeServiceManufacturers';
import { sectionHeaderSx, subHeaderSx } from './inspectionReportFormStyles';

const WATER_TREATMENT_TYPE_OPTIONS = [
  { label: 'Water Softener', value: 'water_softener' },
  { label: 'Reverse Osmosis', value: 'reverse_osmosis' },
  { label: 'Whole House Filter', value: 'whole_house_filter' },
];

interface WaterTreatmentInspectionSectionProps {
  formValues: InspectionFormValues;
  updateField: (field: string, value: unknown) => void;
  waterTreatmentManufacturers: ManufacturerOption[];
  waterTreatmentPhotos: File[];
  onWaterTreatmentPhotosChange: (files: File[]) => void;
}

const WaterTreatmentInspectionSection: React.FC<WaterTreatmentInspectionSectionProps> = ({
  formValues,
  updateField,
  waterTreatmentManufacturers,
  waterTreatmentPhotos,
  onWaterTreatmentPhotosChange,
}) => (
  <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
    <Box sx={{ ...sectionHeaderSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#fff' }}>Water Treatment</Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={formValues.waterTreatmentNA}
            onChange={(e) => updateField('waterTreatmentNA', e.target.checked)}
            sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
          />
        }
        label={<Typography sx={{ color: '#fff', fontSize: '14px' }}>None</Typography>}
      />
    </Box>

    {!formValues.waterTreatmentNA && (
      <Box sx={{ p: 2 }}>
        <Typography sx={subHeaderSx}>Type</Typography>
        <Box sx={{ mb: 2 }}>
          <InspectionRadioGroup
            radioOptions={WATER_TREATMENT_TYPE_OPTIONS}
            value={formValues.waterTreatmentType}
            setValue={(val) => updateField('waterTreatmentType', val)}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth variant="filled">
            <InputLabel>Manufacturer</InputLabel>
            <Select
              value={formValues.waterTreatmentManufacturer}
              onChange={(e) => updateField('waterTreatmentManufacturer', e.target.value as string)}
            >
              {waterTreatmentManufacturers.map((opt) => (
                <MenuItem key={opt.value} value={opt.label}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Typography sx={subHeaderSx}>Inspection Details</Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Visible Condition Of Equipment"
              radioOptions={conditionOptions}
              value={formValues.waterTreatmentVisibleCondition}
              setValue={(val) => updateField('waterTreatmentVisibleCondition', val)}
            />
          </Grid>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Recommended Annual Maintenance"
              radioOptions={yesNoOptions}
              value={formValues.waterTreatmentRecommendedAnnualMaintenance}
              setValue={(val) => updateField('waterTreatmentRecommendedAnnualMaintenance', val)}
            />
          </Grid>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Are Filters Or Salt Required"
              radioOptions={yesNoOptions}
              value={formValues.waterTreatmentFiltersOrSaltRequired}
              setValue={(val) => updateField('waterTreatmentFiltersOrSaltRequired', val)}
            />
          </Grid>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Are There Water Quality Complaints"
              radioOptions={yesNoOptions}
              value={formValues.waterTreatmentWaterQualityComplaints}
              setValue={(val) => updateField('waterTreatmentWaterQualityComplaints', val)}
              invertYesNoColors
            />
          </Grid>
          <Grid item xs={12}>
            <InspectionRadioGroup
              label="Location For Water Treatment Solution"
              radioOptions={yesNoOptions}
              value={formValues.waterTreatmentLocationForSolution}
              setValue={(val) => updateField('waterTreatmentLocationForSolution', val)}
            />
          </Grid>
        </Grid>

        <MediaUploadSection
          label="Water Treatment Photos"
          files={waterTreatmentPhotos}
          onChange={onWaterTreatmentPhotosChange}
        />
      </Box>
    )}

    <Box sx={{ p: 2, pt: !formValues.waterTreatmentNA ? 0 : 2 }}>
      <TextInput
        label="Water Treatment Notes"
        value={formValues.waterTreatmentNotes}
        valueChanged={(val) => updateField('waterTreatmentNotes', val)}
        multiline
        rows={3}
      />
    </Box>
  </Paper>
);

export default WaterTreatmentInspectionSection;
